import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { SendOutlined } from '@mui/icons-material';

import { Box, Collapse, Stack } from '@mui/material';

import { z } from 'zod';

import { EditorContent } from '@tiptap/react';

import { use } from 'react';

import { Or2Button } from '@/components/button/Or2Button';

import { graphql, type DocumentType } from '@/gql';
import { useMutation } from '@/hooks/mutationWithSnackbar';
import { useOr2Editor } from '@/modules/editor/hooks/useOr2Editor';
import { Or2Alert } from '@/components/feedback/Or2Alert';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import { Or2BubbleMenu } from '@/modules/editor/bubble-menu/Or2BubbleMenu';

import { UserAvatarWithQuery } from '@/modules/user/components/UserAvatarWithQuery';

import { PostCommentEmojiButton } from './comment-editor/PostCommentEmojiButton';

export const PostCommentSchema = z.object({
  htmlContent: z.string(),
});

export type PostCommentFormData = z.infer<typeof PostCommentSchema>;

const ADD_COMMENT_TO_POST_MUTATION = graphql(`
  mutation AddCommentToPost($data: AddCommentToPostInput) {
    addCommentToPost(params: $data) {
      post {
        ...postFields
      }
    }
  }
`);

const POST_COMMENT_EDITOR_POST_FRAGMENT = graphql(`
  fragment postCommentEditorPostFragment on Post {
    _id
  }
`);

interface Props {
  post: DocumentType<typeof POST_COMMENT_EDITOR_POST_FRAGMENT>;
}

export const PostCommentEditor = ({ post }: Props) => {
  const { currentUserId } = use(CurrentUserContext);

  const { watch, setValue, handleSubmit, reset } = useForm<PostCommentFormData>(
    {
      defaultValues: {
        htmlContent: '',
      },
      resolver: zodResolver(PostCommentSchema),
    },
  );

  const [addCommentToPost, { loading }] = useMutation(
    ADD_COMMENT_TO_POST_MUTATION,
    {
      completionMessage: 'Added comment to post',
      onCompleted: () => reset(),
    },
  );

  const editor = useOr2Editor({
    editable: loading ? false : true,
    htmlContent: watch('htmlContent'),
    onChange: (htmlContent) => setValue('htmlContent', htmlContent),
  });

  const onSubmit = (data: PostCommentFormData) => {
    addCommentToPost({
      variables: {
        data: {
          htmlContent: data.htmlContent,
          editorContent: null,
          content: null,
          post: post._id,
        },
      },
    });
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ paddingX: 2, width: '100%' }}
      >
        <UserAvatarWithQuery userId={currentUserId} size={40} />
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Or2BubbleMenu editor={editor} disableFileSelector />
          <EditorContent readOnly={loading} editor={editor} />
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          {editor ? <PostCommentEmojiButton editor={editor} /> : null}
          <Or2Button
            disabled={loading || editor?.isEmpty}
            size="small"
            startIcon={<SendOutlined />}
            onClick={handleSubmit(onSubmit)}
          >
            Send
          </Or2Button>
        </Stack>
      </Stack>
      <Collapse in={!editor?.isEmpty}>
        <Box sx={{ px: 2, py: 2 }}>
          <Or2Alert severity="info">
            This comment will be emailed to the original recipients of the post
            and anybody else who has commented
          </Or2Alert>
        </Box>
      </Collapse>
    </>
  );
};
